import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import logo from "../assets/view-r-logo-icon.svg";
import serviceLogo from "../assets/view-r-wordmark.svg";
import designTokens from "../styles/designTokens";

const CONTAINER_FADE_IN_MS = 300;
const LOGO_ENTER_MS = 700;
const TEXT_DELAY_MS = 400;
const TEXT_ENTER_MS = 500;
const EXIT_MS = 400;

const ENTER_COMPLETE_MS = Math.max(
  CONTAINER_FADE_IN_MS,
  LOGO_ENTER_MS,
  TEXT_DELAY_MS + TEXT_ENTER_MS
);

const COMPLETE_MS = ENTER_COMPLETE_MS + EXIT_MS;

// Container fade in animation (Figma style - smooth and quick)
const containerFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// Logo fade in with scale and slight bounce (Figma Smart Animate style)
// Figma typically uses ease-out with slight overshoot for logo animations
const logoFadeInScale = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.85) translateY(8px);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.02) translateY(-2px);
  }
  70% {
    opacity: 1;
    transform: scale(0.98) translateY(1px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

// Text fade in with slide (Figma style - delayed entrance)
// Smooth slide from left with fade
const textFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  60% {
    opacity: 0.6;
    transform: translateX(2px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Fade out animation (smooth exit)
const fadeOut = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.95);
  }
`;

interface LogoMotionProps {
  onComplete?: () => void;
  variant?: "default" | "mobile";
}

const LogoMotion: React.FC<LogoMotionProps> = ({
  onComplete,
  variant = "default",
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      const reducedMotionTimer = setTimeout(() => {
        onCompleteRef.current?.();
      }, 100);

      return () => clearTimeout(reducedMotionTimer);
    }

    // JS timers share the same timeline constants as the CSS animations.
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, ENTER_COMPLETE_MS);

    const completeTimer = setTimeout(() => {
      onCompleteRef.current?.();
    }, COMPLETE_MS);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: designTokens.fill.background.grey20.dark,
        zIndex: 9999,
        px: variant === "mobile" ? 3 : 0,
        animation: isExiting
          ? `${fadeOut} ${EXIT_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
          : `${containerFadeIn} ${CONTAINER_FADE_IN_MS}ms ease-out forwards`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: designTokens.numbers.gap[7], // 7px gap from Figma design
          animation: isExiting ? "none" : "none",
        }}
      >
        {/* Logo Icon - 64x64px from Figma design */}
        <Box
          component="img"
          src={logo}
          alt="View R"
          sx={{
            width:
              variant === "mobile"
                ? 56
                : designTokens.numbers.widthHeight[64],
            height:
              variant === "mobile"
                ? 56
                : designTokens.numbers.widthHeight[64],
            flexShrink: 0,
            animation: isExiting
              ? "none"
              : `${logoFadeInScale} ${LOGO_ENTER_MS}ms cubic-bezier(0.34, 1.3, 0.64, 1) forwards`,
            willChange: "transform, opacity",
          }}
        />
        {/* Text Logo - ServiceLogo image with Figma style animation */}
        {/* Figma design: width 119px (118.999px), height 31px (30.616px) */}
        {/* Using aspect ratio to maintain design proportions */}
        <Box
          component="img"
          src={serviceLogo}
          alt="View R Studio"
          sx={{
            height: variant === "mobile" ? "28px" : "31px",
            width: "auto",
            maxWidth: variant === "mobile" ? "108px" : "119px",
            flexShrink: 0,
            objectFit: "contain",
            animation: isExiting
              ? "none"
              : `${textFadeIn} ${TEXT_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1) ${TEXT_DELAY_MS}ms forwards`,
            opacity: 0,
            willChange: "transform, opacity",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
};

export default LogoMotion;
